import { Location } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Api3Service } from '@app/services/api3.service';
import { AuthService } from '@app/services/auth.service';
import { NavigationService } from '@app/services/navigation.service';
import { Currency } from '@app/shared/currency';
import { dayDiff } from '@app/shared/dateHelper';
import { toError } from '@app/services/error-normalization-utils';
import * as Sentry from '@sentry/angular';

export const STRIPE_SESSION_ID = 'checkout_session';
const SUCCESS_MESSAGE_TIMEOUT = 20000; // 20 seconds

export type Interval = 'month' | 'year' | 'trimester';
export type ProductPrice = {
  id: string;
  nickname: string;
  interval: Interval;
  currency: Currency;
  unitAmountDecimal: string;
  paymentLink: string;
};

export type PurchaseProduct = {
  id: string;
  name: string;
  description: string;
  metadata: object;
  marketing_features: { name: string }[];
  contactUrl?: string;
  productPrices: ProductPrice[];
};

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  authService = inject(AuthService);
  location = inject(Location);
  router = inject(Router);
  api3Service = inject(Api3Service);

  paymentSuccess = signal<boolean>(false);
  subscriptionExpired = signal<boolean>(false);
  customerPortalUrl = signal<string | undefined>(undefined);

  onUserChange = effect(() => {
    const qiraUser = this.authService.currentUser();
    if (qiraUser && this.authService.userIsAdmin()) {
      this._getLinkToCustomerPortal().then((url) => {
        this.customerPortalUrl.set(url);
      });
    }

    const expired = this._subscriptionExpired();
    this.subscriptionExpired.set(expired);
  });

  // This is called on every route change
  async checkForSuccessfulPurchase() {
    const stripeSessionId = new URLSearchParams(window.location.search).get(STRIPE_SESSION_ID);

    if (!stripeSessionId) {
      return;
    }

    // remove the stripe session id from the url
    const newUrl = NavigationService.getUrlWithoutParameter(this.location.path(), STRIPE_SESSION_ID);
    this.location.replaceState(newUrl);
    this.router.navigateByUrl(newUrl);

    const successPurchase = await this._thereWasASuccessfulPurchase(stripeSessionId);
    if (successPurchase) {
      this.paymentSuccess.set(true);
      setTimeout(() => {
        this.paymentSuccess.set(false);
      }, SUCCESS_MESSAGE_TIMEOUT);

      this.router.navigate(['profile', 'me']);
    }
  }

  async requestPaymentProducts(): Promise<{ data: PurchaseProduct[] | undefined; error: any }> {
    const { data, error } = await this.api3Service.api.payment.allActiveProducts();

    if (error) {
      console.error('[PaymentService] Error fetching payment products:', error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'PaymentService',
          feature: 'Payment',
          message: 'Failed to load all payment products.',
        },
      });

      throw error;
    }

    // TODO: FIXME:
    // The EnhancedProductDto is extended with the Stripe product,
    // but we don't seem to have the proper type of it.
    const typedResponse = data as unknown as PurchaseProduct[];

    return { data: typedResponse, error };
  }

  trialLeftDays(): number | undefined {
    const user = this.authService.currentUser();
    if (!user) return undefined;

    const plan = user?.subscription?.plan;
    const trialEnd = user.subscription?.trialEnd;
    const trialEndDate = new Date(trialEnd);

    if (plan !== 'trial' || isNaN(trialEndDate.getTime())) {
      return undefined;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    trialEndDate.setHours(0, 0, 0, 0);
    return dayDiff(trialEndDate, today);
  }

  subscriptionLeftDays(): number | undefined {
    const user = this.authService.currentUser();
    if (!user) return undefined;

    const plan = user?.subscription?.plan;
    const subscriptionEnd = user.subscription?.endOfSub;
    if (plan === 'trial' || !subscriptionEnd || isNaN(new Date(subscriptionEnd).getTime())) {
      return undefined;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dayDiff(new Date(subscriptionEnd), today);
  }

  userHasSubscription() {
    const user = this.authService.currentUser();
    if (!user || !user?.subscription) return false;
    return !!user?.subscription?.plan;
  }

  getUserSubscription() {
    const user = this.authService.currentUser();
    if (!user || !user?.subscription) return null;
    return user?.subscription?.plan;
  }

  private _subscriptionExpired(): boolean {
    const user = this.authService.currentUser();
    if (!user) return false;

    const plan = user.subscription?.plan;

    // no plan means also expired
    if (!plan) return true;

    // trial expired?
    if (plan === 'trial' && this.trialLeftDays() !== undefined && (this.trialLeftDays() ?? 0) < 1) return true;

    // other subscription expired?
    if (plan !== 'trial' && this.subscriptionLeftDays() !== undefined && (this.subscriptionLeftDays() ?? 0) < 1) return true;

    return false;
  }

  private async _thereWasASuccessfulPurchase(stripeSessionId: string | null | undefined): Promise<boolean> {
    if (!stripeSessionId) {
      console.error('[PaymentService] No stripeSessionId provided');
      return false;
    }

    const { data, error } = await this.api3Service.api.payment.missedSessions({
      checkoutSessionId: stripeSessionId,
    });

    if (error) {
      console.error('[PaymentService] Error checking payment status:', error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'PaymentService',
          feature: 'Payment',
          message: 'Failed to check the payment status.',
        },
      });

      return false;
    }

    if (data.success === false) {
      console.error('[PaymentService] Load payment status was not successful:', error.message);
      return false;
    }

    await this.authService.loadUser();
    return true;
  }

  private async _getLinkToCustomerPortal(): Promise<string | undefined> {
    const companyId = this.authService.currentUser()?.company.id;
    if (!companyId) {
      return;
    }

    type ResponseData = {
      data?: {
        url: string;
      };
    };

    const { data, error } = await this.api3Service.api.payment.getCustomerPortal({ qiraCompanyId: companyId });

    if (error) {
      console.error('[PaymentService] Error fetching billing portal:', error);
      return undefined;
    }

    const typedResponse = data as unknown as ResponseData;
    if (typedResponse.data && typedResponse.data.url) {
      return typedResponse.data.url;
    } else {
      console.log('[PaymentService] No customer portal available:', data);
      // It fails, if the user is not yet registered at stripe. So, we return undefined (which is ok)
      return undefined;
    }
  }
}
